import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    apiAssignableUsers,
    apiCreateAssignment,
    apiDeleteAssignment,
    formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle, PhoneCall, Trash2, User as UserIcon } from "lucide-react";
import { fmtTime, formatLong, fromISO } from "@/lib/date";

export default function AssignmentDialog({
    open,
    onOpenChange,
    date, // ISO
    shift,
    project,
    existing,
    userDirectory, // {user_id -> {full_name, role}}
    canEdit,
    onChanged,
}) {
    const [candidates, setCandidates] = useState([]);
    const [userId, setUserId] = useState("");
    const [onCall, setOnCall] = useState(false);
    const [note, setNote] = useState("");
    const [error, setError] = useState("");
    const [warning, setWarning] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !project) return;
        setError("");
        setWarning("");
        setUserId("");
        setOnCall(false);
        setNote("");
        (async () => {
            try {
                const list = await apiAssignableUsers(project.project_id);
                setCandidates(list);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open, project]);

    const alreadyAssignedIds = useMemo(
        () => new Set((existing || []).map((a) => a.user_id)),
        [existing],
    );

    async function submit(e) {
        e.preventDefault();
        setError("");
        setWarning("");
        if (!userId) {
            setError("Choose a consultant to assign.");
            return;
        }
        setSaving(true);
        try {
            const res = await apiCreateAssignment({
                project_id: project.project_id,
                shift_id: shift.shift_id,
                user_id: userId,
                date,
                on_call: onCall,
                note: note || undefined,
            });
            if (res.warning) {
                setWarning(res.warning);
                toast.warning(res.warning);
            } else {
                toast.success("Assigned");
            }
            onChanged?.();
            setUserId("");
            setOnCall(false);
            setNote("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function markNil() {
        setError("");
        setWarning("");
        // Already NIL?
        if ((existing || []).some((a) => a.nil)) {
            setError("This shift is already marked NIL for the day.");
            return;
        }
        setSaving(true);
        try {
            await apiCreateAssignment({
                project_id: project.project_id,
                shift_id: shift.shift_id,
                date,
                nil: true,
                note: note || undefined,
            });
            toast.success("Marked NIL for the day");
            onChanged?.();
            setUserId("");
            setOnCall(false);
            setNote("");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    async function remove(a) {
        if (!window.confirm("Remove this assignment?")) return;
        try {
            await apiDeleteAssignment(a.assignment_id);
            toast.success("Assignment removed");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    if (!shift || !project) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto"
                data-testid="assignment-dialog"
            >
                <DialogHeader>
                    <DialogTitle>
                        {shift.name} · {formatLong(fromISO(date))}
                    </DialogTitle>
                    <DialogDescription>
                        {project.name} · {project.code} ·{" "}
                        {fmtTime(shift.start_time)}–{fmtTime(shift.end_time)}
                    </DialogDescription>
                </DialogHeader>

                {/* Existing */}
                <div className="space-y-1.5" data-testid="existing-assignments">
                    <div className="text-[11px] uppercase tracking-wider text-ntt-muted">
                        Assigned
                    </div>
                    {(existing || []).length === 0 ? (
                        <div className="text-[13px] text-ntt-muted italic py-1">
                            No consultants yet. This shift is uncovered.
                        </div>
                    ) : (
                        existing.map((a) => {
                            if (a.nil) {
                                return (
                                    <div
                                        key={a.assignment_id}
                                        data-testid={`assignment-item-${a.assignment_id}`}
                                        className="flex items-center gap-3 px-3 py-2 border border-dashed border-ntt-border rounded-md bg-[#F4F6F9]"
                                    >
                                        <span className="inline-flex items-center h-7 px-2 rounded bg-white text-[11px] font-semibold text-ntt-muted border border-ntt-border">
                                            NIL
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[13px] font-medium text-[#070F26]">
                                                No assignment needed
                                            </div>
                                            {a.note && (
                                                <div className="text-[11px] text-ntt-muted truncate">
                                                    {a.note}
                                                </div>
                                            )}
                                        </div>
                                        {canEdit && (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => remove(a)}
                                                data-testid={`remove-assignment-${a.assignment_id}`}
                                                className="h-7 text-[12px] text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                );
                            }
                            const u = userDirectory[a.user_id];
                            return (
                                <div
                                    key={a.assignment_id}
                                    data-testid={`assignment-item-${a.assignment_id}`}
                                    className="flex items-center gap-3 px-3 py-2 border border-ntt-border rounded-md bg-white"
                                >
                                    <div className="h-7 w-7 rounded-full bg-[#DBEAF6] text-[#0072BC] text-[11px] font-semibold flex items-center justify-center">
                                        {((u?.full_name || a.user_full_name || "?"))[0]?.toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-medium text-[#070F26] truncate">
                                            {u?.full_name || a.user_full_name || "Unknown user"}
                                        </div>
                                        {a.note && (
                                            <div className="text-[11px] text-ntt-muted truncate">
                                                {a.note}
                                            </div>
                                        )}
                                    </div>
                                    {a.on_call && (
                                        <span
                                            data-testid={`oncall-${a.assignment_id}`}
                                            className="inline-flex items-center gap-1 h-6 px-2 rounded text-[11px] font-medium bg-[#FFC303] text-[#070F26]"
                                        >
                                            <PhoneCall className="h-3 w-3" /> on
                                            call
                                        </span>
                                    )}
                                    {canEdit && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => remove(a)}
                                            data-testid={`remove-assignment-${a.assignment_id}`}
                                            className="h-7 text-[12px] text-red-600 hover:text-red-700"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>

                {/* New assignment form */}
                {canEdit && (
                    <form
                        onSubmit={submit}
                        className="space-y-4 border-t pt-4"
                        data-testid="new-assignment-form"
                    >
                        <div className="text-[11px] uppercase tracking-wider text-ntt-muted">
                            Add assignment
                        </div>
                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Consultant
                            </Label>
                            <Select value={userId} onValueChange={setUserId}>
                                <SelectTrigger
                                    className="h-10"
                                    data-testid="assignment-user-trigger"
                                >
                                    <SelectValue placeholder="Choose a consultant" />
                                </SelectTrigger>
                                <SelectContent>
                                    {candidates.length === 0 && (
                                        <div className="px-3 py-2 text-[12px] text-ntt-muted">
                                            No assignable users. Authorise
                                            consultants on this project.
                                        </div>
                                    )}
                                    {candidates.map((c) => {
                                        const busy = alreadyAssignedIds.has(
                                            c.user_id,
                                        );
                                        return (
                                            <SelectItem
                                                key={c.user_id}
                                                value={c.user_id}
                                                data-testid={`user-option-${c.user_id}`}
                                            >
                                                <span className="inline-flex items-center gap-2">
                                                    <UserIcon className="h-3 w-3 text-ntt-muted" />
                                                    {c.full_name}
                                                    <span className="text-ntt-muted text-[11px]">
                                                        · {c.role}
                                                    </span>
                                                    {busy && (
                                                        <span className="text-[11px] text-[#0072BC]">
                                                            already on this shift
                                                        </span>
                                                    )}
                                                </span>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center gap-3 border border-ntt-border rounded-md px-3 py-2">
                            <Switch
                                checked={onCall}
                                onCheckedChange={setOnCall}
                                data-testid="assignment-oncall-switch"
                            />
                            <div>
                                <div className="text-[13px] font-medium text-[#070F26]">
                                    On call
                                </div>
                                <div className="text-[11px] text-ntt-muted">
                                    Marked with an amber badge on the calendar.
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label className="text-[12px] text-ntt-muted mb-1.5 block">
                                Note (optional)
                            </Label>
                            <Input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="e.g. Covering for Ada until 6 PM"
                                data-testid="assignment-note-input"
                                className="h-10"
                            />
                        </div>

                        {warning && (
                            <div
                                data-testid="assignment-warning"
                                className="flex items-start gap-2 text-[12px] text-[#8a5a00] bg-[#FFF7DC] border border-[#F5DE94] rounded-md px-3 py-2"
                            >
                                <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                                <span>{warning}</span>
                            </div>
                        )}
                        {error && (
                            <div
                                data-testid="assignment-error"
                                className="flex items-start gap-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                <AlertCircle className="h-4 w-4 mt-[1px] shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <DialogFooter className="pt-2 flex flex-col sm:flex-row sm:justify-between gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={markNil}
                                disabled={saving || (existing || []).some((a) => a.nil)}
                                data-testid="mark-nil-btn"
                                className="h-9 text-[12px] text-[#5B6B79] hover:text-[#070F26]"
                                title="Mark this shift as intentionally un-staffed for the day. Does not count as a coverage gap."
                            >
                                Mark shift as NIL
                            </Button>
                            <div className="flex gap-2 sm:ml-auto">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                    data-testid="assignment-close-btn"
                                >
                                    Close
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={saving}
                                    data-testid="assignment-save-btn"
                                    className="bg-[#0072BC] hover:bg-[#005a94] text-white"
                                >
                                    {saving ? "Assigning…" : "Assign"}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
